/**
 * YandexSmartCaptcha для сервиса открытия счёта.
 * Повторяет логику Nuxt-сущности frontend/app/entities/captcha:
 * загрузка скрипта, рендер, получение токена, сброс.
 * Параметры приходят с бэка: initialData → options.captcha.params
 */

const SCRIPT_URL = 'https://smartcaptcha.yandexcloud.net/captcha.js';
const LOAD_TIMEOUT_MS = 5000;
const LOAD_CHECK_MS = 50;
// Событие success может прийти чуть позже закрытия окна с заданием
const CHALLENGE_CLOSE_DELAY_MS = 300;
const NOT_VERIFIED_ERROR = 'SmartCaptcha is not verified';
// Бэк отдаёт булевы параметры строками
const isEnabled = (value) => value === 'true';

/**
 * Загружает скрипт SmartCaptcha.
 * Флаг window.smartCaptchaLoaded общий с расширением aton.yandex-smart-captcha
 * @returns {Promise<void>}
 */
function loadSmartCaptchaScript() {
  return new Promise((resolve, reject) => {
    if (window.smartCaptcha) {
      resolve();
      return;
    }

    if (!window.smartCaptchaLoaded) {
      const script = document.createElement('script');
      script.src = SCRIPT_URL;
      script.defer = true;
      script.onerror = () => {
        window.smartCaptchaLoaded = false;
      };
      document.head.appendChild(script);
      window.smartCaptchaLoaded = true;
    }

    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (window.smartCaptcha) {
        clearInterval(timer);
        resolve();
      } else if (Date.now() - startedAt > LOAD_TIMEOUT_MS) {
        clearInterval(timer);
        reject(new Error('SmartCaptcha script is not loaded'));
      }
    }, LOAD_CHECK_MS);
  });
}

export class AosYandexCaptcha {
  /**
   * @param {HTMLElement} container
   * @param {Object} params - параметры YandexSmartCaptcha::getParams с бэка
   */
  constructor(container, params) {
    this.container = container;
    this.captchaId = null;
    this.renderPromise = null;
    this.pendingTokenRequest = null;
    this.applyParams(params);
  }

  /**
   * Запоминает параметры виджета и показывает контейнер только для видимой капчи
   * @param {Object} params - параметры YandexSmartCaptcha::getParams с бэка
   */
  applyParams(params) {
    params = params || {};
    this.params = {
      sitekey: params.KEY_CLIENT || '',
      hl: params.HL || 'ru',
      test: isEnabled(params.TEST),
      webview: isEnabled(params.WEBVIEW),
      invisible: isEnabled(params.INVISIBLE),
      shieldPosition: params.SHIELD_POSITION || undefined,
      hideShield: isEnabled(params.HIDE_SHIELD),
    };
    this.container.classList.toggle('-visible', !this.params.invisible);
  }

  isInvisible() {
    return this.params.invisible;
  }

  /**
   * Рендерит виджет в контейнер. Повторные вызовы возвращают тот же Promise
   * @returns {Promise<void>}
   */
  render() {
    if (!this.renderPromise) {
      this.renderPromise = loadSmartCaptchaScript()
        .then(() => {
          if (!this.params.sitekey) {
            throw new Error('SmartCaptcha sitekey is empty');
          }
          this.captchaId = window.smartCaptcha.render(this.container, this.params);
          this.subscribe();
        })
        .catch((error) => {
          this.renderPromise = null;
          throw error;
        });
    }

    return this.renderPromise;
  }

  subscribe() {
    const id = this.captchaId;

    window.smartCaptcha.subscribe(id, 'success', (token) => this.resolveToken(token));
    // По рекомендации Яндекса критическую ошибку JS обрабатываем как success
    window.smartCaptcha.subscribe(id, 'javascript-error', () => this.resolveToken(''));
    window.smartCaptcha.subscribe(id, 'network-error', () => this.rejectToken());
    // Пользователь закрыл окно с заданием, не пройдя проверку
    window.smartCaptcha.subscribe(id, 'challenge-hidden', () => {
      setTimeout(() => {
        const token = this.getResponse();
        if (token) {
          this.resolveToken(token);
        } else {
          this.rejectToken();
        }
      }, CHALLENGE_CLOSE_DELAY_MS);
    });
  }

  resolveToken(token) {
    if (this.pendingTokenRequest) {
      this.pendingTokenRequest.resolve(typeof token === 'string' ? token : '');
      this.pendingTokenRequest = null;
    }
  }

  rejectToken() {
    if (this.pendingTokenRequest) {
      this.pendingTokenRequest.reject(new Error(NOT_VERIFIED_ERROR));
      this.pendingTokenRequest = null;
    }
  }

  getResponse() {
    if (this.captchaId === null || !window.smartCaptcha) {
      return '';
    }

    return window.smartCaptcha.getResponse(this.captchaId) || '';
  }

  /**
   * Возвращает токен. Невидимая капча запускает проверку сама,
   * видимая должна быть пройдена пользователем заранее
   * @returns {Promise<string>}
   */
  getToken() {
    return this.render().then(() => {
      const token = this.getResponse();
      if (token) {
        return token;
      }

      if (!this.params.invisible) {
        if (typeof window.smartCaptcha.showError === 'function') {
          window.smartCaptcha.showError(this.captchaId);
        }
        throw new Error(NOT_VERIFIED_ERROR);
      }

      return new Promise((resolve, reject) => {
        this.pendingTokenRequest = { resolve, reject };
        window.smartCaptcha.execute(this.captchaId);
      });
    });
  }

  /**
   * Сброс после использования токена: токен одноразовый
   */
  reset() {
    this.rejectToken();
    if (this.captchaId !== null && window.smartCaptcha) {
      window.smartCaptcha.reset(this.captchaId);
    }
  }

  destroy() {
    this.rejectToken();
    this.renderPromise = null;
    if (this.captchaId !== null && window.smartCaptcha) {
      window.smartCaptcha.destroy(this.captchaId);
    }
    this.captchaId = null;
  }

  /**
   * Пересоздаёт виджет с новыми параметрами (бэк мог переключить на усложнённую капчу)
   * @param {Object} params
   * @returns {Promise<void>}
   */
  restart(params) {
    const renderPromise = this.renderPromise || Promise.resolve();

    return renderPromise
      .catch(() => {})
      .then(() => {
        this.destroy();
        this.applyParams(params);
        return this.render();
      });
  }
}
